import type { QueryClient } from '@tanstack/react-query';
import { ServerStateKeysEnum } from '@/common/apis/serverStateKeysEnum';

/**
 * Placeholder profile pieces built from the doctor's search result (the whole API item, not just what
 * the card shows). Shown instantly on search → profile, then replaced in the background by each
 * piece's fresh response (react-query `placeholderData`, so every query still fetches). Never used
 * for analytics: `useProgressiveProfile` only reports `complete` once no piece is a placeholder.
 */
export type SeedPieces = {
  fullName?: { name: string; family: string };
  expertise?: any[];
  image?: { image: string };
  centers?: any[];
  rateDetails?: any;
  pageView?: number;
  keywordTags?: string[];
  medicalCode?: string;
  waitingTime?: any[];
  onlineServices?: any[];
};

const CONSULT_CENTER_ID = '5532';

const decode = (s: string) => {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
};

export const findSearchDoctor = (queryClient: QueryClient, slug: string): any | null => {
  const wanted = decode(slug);
  for (const [, data] of queryClient.getQueriesData([ServerStateKeysEnum.Search])) {
    const results = (data as any)?.search?.result;
    if (!Array.isArray(results)) continue;
    const hit = results.find((r: any) => r?.type === 'doctor' && typeof r?.slug === 'string' && decode(r.slug) === wanted);
    if (hit) return hit;
  }
  return null;
};

const fixed = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v.toFixed(1) : null);

/** Search result item → pieces in the exact shapes the profile fetchers return. */
export const seedPiecesFromSearchDoctor = (d: any): SeedPieces => {
  if (!d) return {};
  const out: SeedPieces = {};

  const display = typeof d.display_name === 'string' ? d.display_name.trim() : '';
  const name = typeof d.name === 'string' ? d.name.trim() : '';
  if (display) {
    const family = name && display.startsWith(name) ? display.slice(name.length).trim() : display.split(' ').slice(1).join(' ');
    out.fullName = { name: name || display.split(' ')[0], family };
  }

  if (typeof d.image === 'string' && d.image) out.image = { image: d.image };

  if (Array.isArray(d.expertises) && d.expertises.length) {
    out.expertise = d.expertises.map((e: any) => ({
      alias_title: e?.alias_title ?? '',
      degree: e?.degree ?? null,
      expertise: e?.expertise ?? null,
      groups: (e?.expertise_groups ?? []).map((g: any) => ({ name: g?.name })),
      graduation_date: d.graduation_date ?? null,
    }));
  }

  if (Array.isArray(d.centers) && d.centers.length) {
    out.centers = d.centers.map((c: any) => ({
      id: c?.id,
      name: c?.name,
      display_number: typeof c?.display_number === 'string' ? c.display_number.split('|').filter(Boolean) : [],
      tell: [],
      address: c?.address ?? '',
      type_id: c?.center_type,
      status: c?.status,
      server_id: c?.server_id,
      user_center_id: c?.user_center_id,
      active_booking: c?.active_booking,
      has_bookable_services: !!c?.active_booking,
      city: { id: 0, name: c?.city_name ?? '', slug: '', province: { id: 0, name: c?.province_name ?? '', slug: '' } },
      location: { lat: c?.map?.lat ?? null, lon: c?.map?.lon ?? null },
    }));
  }

  const r = d.rate_info;
  if (r && typeof r === 'object') {
    out.rateDetails = {
      averageRates: {
        average_quality_of_treatment: fixed(r.quality_of_treatment),
        average_doctor_encounter: fixed(r.doctor_encounter),
        average_explanation_of_issue: fixed(r.explanation_of_issue),
      },
      hide_rates: false,
      countOfFeedbacks: typeof r.rates_count === 'number' ? r.rates_count : null,
    };
  }

  if (typeof d.number_of_visits === 'number' && Number.isFinite(d.number_of_visits)) out.pageView = d.number_of_visits;

  // The profile fetches keyword tags FROM the search API (getDoctorKeywordTags) — same field, same value.
  const tags = d.keyword_tags_user_priority_1 ?? d.keyword_tags?.user_priority_1;
  if (Array.isArray(tags)) out.keywordTags = tags.map((t: unknown) => String(t ?? '').replace(/\s+/g, ' ').trim()).filter(Boolean);

  if (typeof d.medical_code === 'string' && d.medical_code.trim()) out.medicalCode = d.medical_code.trim();

  // Online visit: the search centers list omits the consult center (5532) but `center_id` and
  // `consult_services` carry it. `free_price` is in rials — the same unit as service.price.
  const consultService = Array.isArray(d.consult_services) ? d.consult_services.find((c: any) => c?.id && c?.free_price != null) : null;
  if (consultService && Array.isArray(d.center_id) && d.center_id.includes(CONSULT_CENTER_ID)) {
    if (out.centers && !out.centers.some(c => String(c.id) === CONSULT_CENTER_ID)) {
      out.centers = [
        ...out.centers,
        {
          id: CONSULT_CENTER_ID,
          name: 'ویزیت آنلاین',
          display_number: [],
          tell: [],
          address: '',
          type_id: 3,
          status: 1,
          server_id: d.server_id,
          active_booking: !!d.consult_active_booking,
          has_bookable_services: !!d.consult_active_booking,
          city: { id: 0, name: '', slug: '', province: { id: 0, name: '', slug: '' } },
          location: { lat: null, lon: null },
        },
      ];
    }
    out.onlineServices = [
      {
        id: consultService.id,
        can_booking: !!d.consult_active_booking,
        can_request: false,
        duration: null,
        request_description: null,
        terms_and_conditions: null,
        service: { id: consultService.id, title: 'ویزیت آنلاین', price: consultService.free_price, description: null, service_type_id: 1, server_id: d.server_id },
      },
    ];
  }

  const w = d.waiting_time_info;
  if (w && typeof w === 'object' && w.center_id) out.waitingTime = [{ ...w }];
  return out;
};
